import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/admin/Header';
import Sidebar from '../../components/admin/Sidebar';
import Footer from '../../components/admin/Footer';
import axios from 'axios';
import { 
  ArrowLeft,
  Save,
  Shield,
  ChartBar,
  CloudSun,
  TrendingUp,
  Map,
  Bell,
  Building,
  Check,
  Filter,
  X,
  Loader2
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ContentAccessPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessData, setAccessData] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // API base URL
  const apiUrl = process.env.REACT_APP_API_URL;
  const axiosConfig = {
    baseURL: apiUrl,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
    }
  };

  // Content access options
  const contentOptions = [
    {
      id: 'LongTermGrowthPortfolio',
      name: 'Long Term Growth Portfolio',
      description: 'Access to long-term investment portfolio',
      icon: TrendingUp,
      color: 'bg-blue-500'
    },
    {
      id: 'AllWeatherPortfolio',
      name: 'All Weather Portfolio',
      description: 'Access to all-weather investment portfolio',
      icon: CloudSun,
      color: 'bg-green-500'
    },
    {
      id: 'DividendGrowthPortfolio',
      name: 'Dividend Growth Portfolio',
      description: 'Access to dividend growth portfolio',
      icon: ChartBar,
      color: 'bg-yellow-500'
    },
    {
      id: 'HeatMapDashboard',
      name: 'Heat Map Dashboard',
      description: 'Access to market heat map dashboard',
      icon: Map,
      color: 'bg-red-500'
    },
    {
      id: 'IntradayCallsDashboard',
      name: 'Intraday Calls Dashboard',
      description: 'Access to intraday trading calls',
      icon: Bell,
      color: 'bg-purple-500'
    },
    {
      id: 'RegisteredRAPlateform',
      name: 'Registered RA Platform',
      description: 'Access to registered research analyst platform',
      icon: Building,
      color: 'bg-indigo-500'
    },
    {
      id: 'IPOResearch',
      name: 'IPO Research Platform',
      description: 'Access comprehensive IPO research',
      icon: Building,
      color: 'bg-indigo-500'
    },
    {
      id: 'SEBIMonthlyRecommendations',
      name: 'Nifty 50',
      description: 'Nifty 50 stock recommendations as per SEBI guidelines',
      icon: Shield,
      color: 'bg-pink-500'
    },
   {
      id: 'FundamentalScreener',
      name: 'Fundamental Screener',
      description: 'Advanced fundamental analysis and stock screening tools',
      icon: Filter, // Filter icon works well for screening
      color: 'bg-teal-500' // Professional teal color
    },
    {
      id: 'ReturnScreener',
      name: 'Return Screener',
      description: 'Analyze and screen stocks based on return performance metrics',
      icon: TrendingUp, // TrendingUp icon fits return/performance
      color: 'bg-indigo-500' // Clean, professional indigo color
    }

  ];

  useEffect(() => {
    fetchUserAndAccessData();
  }, [userId]);

  const fetchUserAndAccessData = async () => {
    try {
      setLoading(true);
      
      // Fetch user details
      const userResponse = await axios.get(`/api/auth/users/${userId}`, axiosConfig);
      
      if (userResponse.data.success) {
        setUser(userResponse.data.data);
        
        // Fetch content access data
        try {
          const accessResponse = await axios.get(`/api/content-access/${userId}`, axiosConfig);
          if (accessResponse.data.success) {
            setAccessData(accessResponse.data.data);
          } else {
            // Initialize with default values if no record exists
            const defaultAccess = {};
            contentOptions.forEach(option => {
              defaultAccess[option.id] = 0;
            });
            setAccessData(defaultAccess);
          }
        } catch (error) {
          console.error('Error fetching access data:', error);
          // Initialize with default values
          const defaultAccess = {};
          contentOptions.forEach(option => {
            defaultAccess[option.id] = 0;
          });
          setAccessData(defaultAccess);
        }
      } else {
        toast.error('User not found');
        navigate('/admin/users');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load user data');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const toggleAccess = (optionId) => {
    setAccessData(prev => ({
      ...prev,
      [optionId]: prev[optionId] === 1 ? 0 : 1
    }));
  };

  const saveAccessChanges = async () => {
    try {
      setSaving(true);
      
      const response = await axios.put(
        `/api/content-access/${userId}`,
        accessData,
        axiosConfig
      );
      
      if (response.data.success) {
        toast.success('Access permissions updated successfully');
      } else {
        toast.error('Failed to update access permissions');
      }
    } catch (error) {
      console.error('Error saving access changes:', error);
      toast.error(error.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectAll = () => {
    const allSelected = {};
    contentOptions.forEach(option => {
      allSelected[option.id] = 1;
    });
    setAccessData(allSelected);
  };

  const handleDeselectAll = () => {
    const noneSelected = {};
    contentOptions.forEach(option => {
      noneSelected[option.id] = 0;
    });
    setAccessData(noneSelected);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <ToastContainer position="bottom-right" autoClose={3000} />
      
      <div className="flex h-screen">
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} />
        
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-h-screen overflow-hidden lg:ml-64">
          {/* Header */}
          <Header toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            {/* Page Header */}
            <div className="mb-6 md:mb-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex items-center">
                  <button
                    onClick={() => navigate('/admin/users')}
                    className="mr-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                      Content Access Management
                    </h1>
                    <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1 md:mt-2">
                      Managing access for {user?.username} ({user?.email})
                    </p>
                  </div>
                </div>
                
                <div className="flex space-x-3 mt-4 md:mt-0">
                  <button
                    onClick={handleSelectAll}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Select All
                  </button>
                  <button
                    onClick={handleDeselectAll}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Deselect All
                  </button>
                  <button
                    onClick={saveAccessChanges}
                    disabled={saving || !accessData}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* User Info Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6">
              <div className="flex items-center">
                <div className="h-16 w-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">
                    {user?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="ml-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {user?.username}
                  </h2>
                  <div className="flex flex-wrap gap-4 mt-2">
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Email:</span>
                      <span className="ml-2">{user?.email}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Phone:</span>
                      <span className="ml-2">{user?.phone}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Status:</span>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs ${user?.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {user?.status}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Member:</span>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs ${user?.is_member === 1 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                        {user?.is_member === 1 ? 'Premium' : 'Basic'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Access Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {contentOptions.map((option) => {
                const Icon = option.icon;
                const isActive = accessData?.[option.id] === 1;
                
                return (
                  <div
                    key={option.id}
                    onClick={() => toggleAccess(option.id)}
                    className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 cursor-pointer transition-all duration-300 hover:shadow-md ${
                      isActive 
                        ? 'border-2 border-blue-500 ring-2 ring-blue-500/20' 
                        : 'border border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center">
                        <div className={`${option.color} h-12 w-12 rounded-lg flex items-center justify-center`}>
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {option.name}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {option.description}
                          </p>
                        </div>
                      </div>
                      
                      <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${
                        isActive 
                          ? 'bg-blue-500 border-blue-500' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {isActive && <Check className="h-4 w-4 text-white" />}
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${
                          isActive 
                            ? 'text-blue-600 dark:text-blue-400' 
                            : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {isActive ? 'Access Granted' : 'Access Restricted'}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          isActive 
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' 
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary Card */}
            {accessData && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Access Summary
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {contentOptions.filter(option => accessData[option.id] === 1).length}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Active Access
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                      {contentOptions.filter(option => accessData[option.id] === 0).length}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Restricted Access
                    </div>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {Math.round((contentOptions.filter(option => accessData[option.id] === 1).length / contentOptions.length) * 100)}%
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Access Coverage
                    </div>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {contentOptions.length}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Total Features
                    </div>
                  </div>
                </div>
              </div>
            )}
          </main>

          {/* Footer */}
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default ContentAccessPage;